import {
  AppHeaderLogo,
  HeaderAccent,
  SideMenuCompactProvider,
  useDisplayModeContext,
} from '@act/storybook';
import { useMobile } from '@act/utilities/mobile-context';
import { Box, useTheme } from '@mui/material';
import * as m from 'motion/react-m';
import { ReactElement, ReactNode } from 'react';
import ViewAsBanner from 'react-components/header/ViewAsBanner';
import SideMenuDrawer from '../SideMenuDrawer/SideMenuDrawer';
import {
  COMPACT_GRID_WIDTH,
  EXPANDED_GRID_WIDTH,
  HEADER_HEIGHT,
} from './constants';

interface AppGridProps {
  headerContent?: ReactElement;
  mainContent?: ReactNode;
  sideMenuContent?: ReactElement;
  hasContinuousHeader?: boolean;
  isOnlyContentPage?: boolean;
  /** When true, the side menu always uses compact layout regardless of displayMode context */
  sideMenuAlwaysCompact?: boolean;
}

const AppGrid = ({
  headerContent,
  mainContent,
  sideMenuContent,
  hasContinuousHeader = false,
  isOnlyContentPage = false,
  sideMenuAlwaysCompact = true,
}: AppGridProps) => {
  const theme = useTheme();
  const { isMobile } = useMobile();
  const { displayMode, setIsAnimating } = useDisplayModeContext();
  const isCompact = sideMenuAlwaysCompact || displayMode === 'compact';

  const gridTemplateAreas = hasContinuousHeader
    ? '"header header" "sidemenu main"'
    : '"branding header" "sidemenu main"';

  // Mobile layout or member-only page - hide left section
  const mobileGridTemplateAreas = hasContinuousHeader
    ? '"header header" "main main"'
    : '"header header" "main main"';

  // Determine if we should show the side menu
  const showSideMenu = !isMobile && !isOnlyContentPage;

  return (
    <>
      {/* Mobile Drawer */}
      {isMobile && !isOnlyContentPage && (
        <SideMenuDrawer sideMenuContent={sideMenuContent} />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateAreas:
            isMobile || isOnlyContentPage
              ? mobileGridTemplateAreas
              : gridTemplateAreas,
          gridTemplateColumns:
            isMobile || isOnlyContentPage
              ? '1fr'
              : isCompact
                ? `${COMPACT_GRID_WIDTH} 1fr`
                : `${EXPANDED_GRID_WIDTH} 1fr`,
          gridTemplateRows: 'auto 1fr',
          height: '100%',
          overflow: 'hidden',
          transition: 'grid-template-columns 0.5s ease-in-out',
          paddingBottom: isMobile
            ? [
                'constant(safe-area-inset-bottom)',
                'env(safe-area-inset-bottom)',
              ]
            : 0,
        }}
      >
        {hasContinuousHeader ? (
          <Box
            sx={{
              gridArea: 'header',
              overflow: 'hidden',
              height: HEADER_HEIGHT,
            }}
          >
            <AppHeaderLogo disableAnimations />
            {headerContent}
          </Box>
        ) : (
          <>
            {/* Branding section - hidden on mobile and member-only page */}
            {showSideMenu && (
              <Box
                sx={{
                  gridArea: 'branding',
                  display: 'grid',
                  placeItems: 'center',
                  borderRight: `1px solid ${theme.palette.strokePrimary}`,
                  borderBottom: `1px solid ${theme.palette.strokePrimary}`,
                }}
              >
                {sideMenuAlwaysCompact ? (
                  <SideMenuCompactProvider>
                    <AppHeaderLogo />
                  </SideMenuCompactProvider>
                ) : (
                  <AppHeaderLogo />
                )}
              </Box>
            )}
            {headerContent && (
              <Box
                sx={{
                  gridArea: 'header',
                  overflow: 'hidden',
                  height: HEADER_HEIGHT,
                }}
              >
                {headerContent}
              </Box>
            )}
          </>
        )}

        {/* Side menu - hidden on mobile and member-only page */}
        {sideMenuContent && showSideMenu && (
          <m.div
            animate={{
              width: isCompact ? COMPACT_GRID_WIDTH : EXPANDED_GRID_WIDTH,
            }}
            transition={{
              type: 'tween',
              duration: 0.5,
            }}
            style={{
              gridArea: 'sidemenu',
              overflow: 'visible',
              minHeight: 0,
            }}
            onAnimationStart={() => {
              if (setIsAnimating) setIsAnimating(true);
            }}
            onAnimationComplete={() => {
              if (setIsAnimating) setIsAnimating(false);
            }}
          >
            {sideMenuAlwaysCompact ? (
              <SideMenuCompactProvider>
                {sideMenuContent}
              </SideMenuCompactProvider>
            ) : (
              sideMenuContent
            )}
          </m.div>
        )}
        {mainContent && (
          <Box
            sx={{
              gridArea: 'main',
              overflow: isMobile ? 'auto' : 'hidden',
              minHeight: 0,
              backgroundColor: theme.palette.neutralLight.main,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <HeaderAccent />
            <ViewAsBanner />
            <Box
              sx={{
                padding: '16px',
                flex: 1,
                overflow: isMobile ? 'auto' : 'hidden',
              }}
            >
              {mainContent}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AppGrid;
